import { AbstractControl } from '@angular/forms';
import { Observable, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

// live username check for the add-user dialogs. The check fires only when the
// admin PAUSES typing (debounce) — continuous typing or backspacing sends no
// request — and only once the name has at least 5 characters.
export class UsernameAvailability {
  static readonly MIN_LENGTH = 5;

  // idle = nothing typed · short = under 5 chars · checking = request running
  status: 'idle' | 'short' | 'checking' | 'available' | 'taken' = 'idle';

  private sub?: Subscription;
  private lastChecked = '';

  // fetch = a service call returning { data: { available: boolean } }
  constructor(private readonly fetch: (userName: string) => Observable<any>) {}

  attach(control: AbstractControl): void {
    this.sub = control.valueChanges
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe((value) => this.check(String(value ?? '')));
  }

  destroy(): void {
    this.sub?.unsubscribe();
  }

  get message(): string {
    switch (this.status) {
      case 'short':
        return 'Username must have at least 5 characters.';
      case 'checking':
        return 'Checking availability…';
      case 'available':
        return 'Username is available.';
      case 'taken':
        return 'This username is already taken.';
      default:
        return '';
    }
  }

  private check(value: string): void {
    const name = value.trim();
    if (!name) {
      this.status = 'idle';
      return;
    }
    if (name.length < UsernameAvailability.MIN_LENGTH) {
      this.status = 'short';
      return;
    }
    this.status = 'checking';
    this.lastChecked = name;
    this.fetch(name).subscribe({
      next: (res: any) => {
        // ignore an answer for an older value (the admin typed on)
        if (this.lastChecked !== name) return;
        this.status = res?.data?.available ? 'available' : 'taken';
      },
      error: () => {
        if (this.lastChecked !== name) return;
        this.status = 'idle';
      },
    });
  }
}
